import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { WidgetCategoriesStats } from "~/api/Api";
import { hasKeywords } from "~/components/dashboard/types";
import { FEATURE_FLAGS, useFeatureFlag } from "~/featureFlags";
import { isWhiteLabel } from "~/utils/white-label";

import { apiClient } from "../../../api/ApiClient";
import { useSiteContext } from "../../../contexts/SiteContext";
import VisibilityPerformanceCard from "../../visibilityPerformance/VisibilityPerformanceCard";
import styles from "./Analytics.module.scss";
import Automations from "./automations/Automations";
import DailyGoals from "./dailyGoals/DailyGoals";
import Keywords from "./keywords/Keywords";

const Analytics = () => {
  const { id: rawId } = useParams();
  const siteContext = useSiteContext();
  const site = siteContext?.site?.site;
  const siteId = rawId ? Number(rawId) : undefined;
  const focusKeywordId = site?.focus_keyword_id ?? undefined;
  const canLoadKeyword = siteId !== undefined && focusKeywordId !== undefined;

  const {
    data: dataUniqueRecommendations,
    isLoading: isUniqueRecommendationsLoading,
  } = useQuery({
    enabled: !!siteId,
    queryKey: ["widget/categories_stats"],
    queryFn: async () => {
      const response = await apiClient.sites.widgetCategoriesStatsDetail(
        siteId!,
      );

      return response?.data?.["widget/categories_stats"] as
        | WidgetCategoriesStats
        | undefined;
    },
  });

  const {
    data: keywordsRankingDetail,
    isLoading: isKeywordsRankingDetailLoading,
  } = useQuery({
    queryKey: ["keyword_ranking", siteId, focusKeywordId],
    queryFn: async () => {
      const response = await apiClient.sites.keywordsRankingDetail(
        siteId!,
        focusKeywordId!,
      );

      return response?.data?.keyword_ranking;
    },
    enabled: canLoadKeyword,
  });

  const {
    data: keywordsHistoricalRankingDetail,
    isLoading: isKeywordsHistoricalRankingDetailLoading,
  } = useQuery({
    queryKey: ["keyword_historical_ranking", siteId, focusKeywordId],
    queryFn: async () => {
      const response = await apiClient.sites.keywordsHistoricalRankingDetail(
        siteId!,
        focusKeywordId!,
      );

      return response?.data?.keyword_historical_ranking;
    },
    enabled: canLoadKeyword,
  });

  const { data: siteDailyGoals, isLoading: isSiteDailyGoalsLoading } = useQuery(
    {
      queryKey: ["site_daily_goals", siteId],
      queryFn: async () => {
        const response = await apiClient.sites.dailyGoalsDetail(Number(siteId));

        return response?.data?.site_daily_goals;
      },
      enabled: !!siteId && !isWhiteLabel(),
    },
  );

  const { data: siteStats, isLoading: isSiteStatsLoading } = useQuery({
    queryKey: ["site_stats", siteId],
    queryFn: async () => {
      const response = await apiClient.sites.statsDetail(Number(siteId));

      return response?.data?.site_stats;
    },
    enabled: !!siteId,
  });
  const { data: topKeywords, isLoading: isTopKeywordsLoading } = useQuery({
    queryKey: ["top_keywords", siteId],
    queryFn: async () => {
      const response = await apiClient.sites.keywordsTopDetail(Number(siteId), {
        "page[size]": 5,
      });

      return response?.data?.keywords;
    },
    enabled: !!siteId,
  });

  const isAutomationsAllowed = useFeatureFlag(
    FEATURE_FLAGS.SITES_RECOMMENDATIONS,
  );
  const isKeywordsAllowed = useFeatureFlag(FEATURE_FLAGS.SITES_KEYWORDS);

  return (
    <div className={styles.analytics}>
      <VisibilityPerformanceCard site={site} />
      {!isWhiteLabel() && (
        <DailyGoals
          siteDailyGoals={siteDailyGoals ?? []}
          isSiteDailyGoalsLoading={isSiteDailyGoalsLoading}
        />
      )}
      {isAutomationsAllowed && (
        <Automations
          siteStats={siteStats}
          uniqueRecommendations={
            dataUniqueRecommendations ?? ({} as WidgetCategoriesStats)
          }
          isUniqueRecommendationsLoading={isUniqueRecommendationsLoading}
          isSiteStatsLoading={isSiteStatsLoading}
        />
      )}
      {hasKeywords(siteStats) && isKeywordsAllowed && (
        <Keywords
          siteStats={siteStats}
          isKeywordsHistoricalRankingDetailLoading={
            isKeywordsHistoricalRankingDetailLoading
          }
          isKeywordsRankingDetailLoading={isKeywordsRankingDetailLoading}
          keywordsHistoricalRankingDetail={keywordsHistoricalRankingDetail}
          isSiteStatsLoading={isSiteStatsLoading}
          keywordsRankingDetail={keywordsRankingDetail}
          topKeywords={topKeywords}
          isTopKeywordsLoading={isTopKeywordsLoading}
        />
      )}
    </div>
  );
};

export default Analytics;
